import React, { FC, useEffect, useRef, useState } from "react";
import { Button, Spin, message } from "antd";
import { DesktopOutlined } from "@ant-design/icons";
import { TpaPortalConfig } from "../../../hooks/useTpaPortalConfig";
import {
  focusTpaPortalPopup,
  openTpaPortalPopup,
} from "../../../utils/openTpaPortalPopup";
import "./PortalWebView.css";

type PortalWebViewProps = {
  config: TpaPortalConfig | null;
  loading?: boolean;
};

/** Compact launcher — single Open portal button (no large iframe panel). */
const PortalWebView: FC<PortalWebViewProps> = ({ config, loading }) => {
  const [popupOpen, setPopupOpen] = useState(false);
  const popupRef = useRef<Window | null>(null);
  const popupPollRef = useRef<ReturnType<typeof setInterval> | null>(null);

  const clearPopupPoll = () => {
    if (popupPollRef.current) {
      clearInterval(popupPollRef.current);
      popupPollRef.current = null;
    }
  };

  const watchPopup = (popup: Window) => {
    clearPopupPoll();
    popupPollRef.current = setInterval(() => {
      if (!popup || popup.closed) {
        clearPopupPoll();
        popupRef.current = null;
        setPopupOpen(false);
      }
    }, 800);
  };

  useEffect(() => () => clearPopupPoll(), []);

  const openControlledPopup = () => {
    if (!config?.portalUrl) return;

    if (popupRef.current && !popupRef.current.closed) {
      focusTpaPortalPopup(popupRef.current);
      setPopupOpen(true);
      message.info("Portal window is already open — focused it.");
      return;
    }

    const { popup, blocked } = openTpaPortalPopup(config.portalUrl);
    if (blocked || !popup) {
      message.warning(
        "Popup blocked by the browser. Allow popups for GSeven, then try again."
      );
      setPopupOpen(false);
      return;
    }

    popupRef.current = popup;
    setPopupOpen(true);
    watchPopup(popup);
  };

  if (loading) {
    return (
      <div className="tpa-portal-open-bar">
        <Spin size="small" />
        <span>Loading portal…</span>
      </div>
    );
  }

  if (!config) return null;

  return (
    <div className="tpa-portal-open-bar">
      <div className="tpa-portal-open-bar-meta">
        <span className="tpa-portal-open-bar-label">
          {config.schemeName || config.portalName}
        </span>
        {config.userName ? (
          <span className="tpa-portal-open-bar-hint">
            Login: {config.userName}
          </span>
        ) : null}
        {popupOpen ? (
          <span className="tpa-portal-badge tpa-portal-badge-live">
            POPUP OPEN
          </span>
        ) : null}
      </div>
      <Button
        type="primary"
        icon={<DesktopOutlined />}
        onClick={openControlledPopup}
      >
        {popupOpen ? "Focus portal" : "Open portal window"}
      </Button>
    </div>
  );
};

export default PortalWebView;
